import React, { useCallback, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import type { AppDispatch } from 'src/client/store'
import { OfferListFilterSubmit } from './OfferListFilterSidebar'
import { fetchOfferListFilters } from 'actions/OfferActions'
import { OfferListSortOption } from 'constants/offerListFilters'
import { fireInteractionEvent } from 'api/googleTagManager'
import { frontPageSortControlOpen, frontPageSortControlSelect } from 'analytics/eventDefinitions'
import { replace } from 'connected-react-router'
import qs from 'qs'
import OfferListFilters from 'components/OfferList/OfferListFilter/OfferListFilters'
import getOfferListKey from 'lib/offerListKey'
import { fetchHotelPopularHolidayTypes } from 'actions/DestinationAlertsActions'
import Heading from 'components/Luxkit/Typography/Heading'
import config from 'constants/config'
import { LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import {
  trackFrontPageClientEvent,
  FrontPageEvent,
} from 'analytics/snowplow-events/frontPageEvents'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'

const DEFAULT_OFFER_LIST_HEADING = <>Explore today's <Heading variant="heading2alt" as="span">exclusive</Heading> offers</> as any

interface Props {
  baseFilters?: App.OfferListFilters;
  existingFilters: App.OfferListFilters;
  types: Array<App.OfferListOfferType>;
  mapViewPath?: string;
  listViewPath?: string;
  sortOptions?: Array<OfferListSortOption>;
  offerListFilters: Record<string, App.OfferListFilterOptions>;
  showPopularHolidayTypesFilterToggles?: boolean;
  heading?: React.ReactNode;
  standaloneCruiseEnabled: boolean;
}

function OfferListFrontPageFilters(props: Props) {
  const {
    baseFilters,
    offerListFilters,
    sortOptions,
    types,
    existingFilters,
    mapViewPath,
    listViewPath,
    showPopularHolidayTypesFilterToggles,
    heading,
    standaloneCruiseEnabled,
  } = props

  const isFilterTogglesShown = useMemo(() =>
    ((types.includes('tour') && types.includes('tour_v2') && !standaloneCruiseEnabled) || showPopularHolidayTypesFilterToggles)
  , [types, showPopularHolidayTypesFilterToggles, standaloneCruiseEnabled])

  const isSideBarFilterHolidayTypeShown = useMemo(() =>
    !(types.includes('tour') && types.includes('tour_v2') && standaloneCruiseEnabled)
  , [types, standaloneCruiseEnabled])

  const dispatch = useDispatch<AppDispatch>()

  const availableFilters = useMemo(() => {
    const key = getOfferListKey({
      ...baseFilters,
      offerTypes: types,
    })
    return offerListFilters[key]
  }, [offerListFilters, types, baseFilters])

  useEffect(() => {
    dispatch(fetchOfferListFilters({
      ...baseFilters,
      offerTypes: types,
    }))
  }, [baseFilters, dispatch, types])

  useEffect(() => {
    if (types.some(type => LE_CONTRACTED_HOTEL_TYPES.includes(type as any))) {
      dispatch(fetchHotelPopularHolidayTypes())
    }
  }, [dispatch, types])

  const onSortOpen = () => {
    fireInteractionEvent(frontPageSortControlOpen())
  }

  const onSortSelect = (sortOption: OfferListSortOption) => {
    fireInteractionEvent(frontPageSortControlSelect(sortOption.label))
  }

  const onFilterApply = useCallback((values: OfferListFilterSubmit) => {
    const nextFilters: App.OfferListFilters = {}
    if (values.destinations?.length) {
      nextFilters.locations = values.destinations
    }

    if (values.holidayTypes?.length) {
      nextFilters.holidayTypes = values.holidayTypes
    }

    if (values.amenities?.length) {
      nextFilters.amenities = values.amenities
    }

    if (values.sortBy && values.sortBy !== 'recommended') {
      nextFilters.sortBy = values.sortBy
    }

    trackFrontPageClientEvent(FrontPageEvent.FILTERS_APPLIED, nextFilters)

    const nextQueryParam = qs.stringify(nextFilters as any, { arrayFormat: 'comma' })

    dispatch(replace({ search: nextQueryParam }))
  }, [dispatch])

  const resetFilters = () => {
    trackFrontPageClientEvent(FrontPageEvent.FILTERS_RESET)
    dispatch(replace({ search: '' }))
  }

  const onViewSwitch = () => {
    if (mapViewPath) {
      trackFrontPageClientEvent(FrontPageEvent.MAP_VIEW_OPENED)
    } else if (listViewPath) {
      trackFrontPageClientEvent(FrontPageEvent.LIST_VIEW_OPENED)
    }
  }

  return (
    <OfferListFilters
      heading={heading || config.offerListHeadingText || DEFAULT_OFFER_LIST_HEADING}
      availableFilters={availableFilters}
      sortOptions={sortOptions}
      existingFilters={existingFilters}
      mapViewPath={mapViewPath}
      listViewPath={listViewPath}
      isFilterTogglesShown={isFilterTogglesShown}
      isSideBarFilterHolidayTypeShown={isSideBarFilterHolidayTypeShown}
      showPopularHolidayTypesFilterToggles={showPopularHolidayTypesFilterToggles}
      onSortOpen={onSortOpen}
      onSortSelect={onSortSelect}
      onFilterApply={onFilterApply}
      onFilterReset={resetFilters}
      onViewSwitch={onViewSwitch}
    />
  )
}

OfferListFrontPageFilters.defaultProps = {
  existingFilters: {},
  sortOptions: [],
  showPopularHolidayTypesFilterToggles: false,
  showToggleFilter: true,
}

function mapStateToProps(state: App.State) {
  return {
    offerListFilters: state.offer.offerListFilterOptions,
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
}

export default connect(mapStateToProps)(OfferListFrontPageFilters)
